import { Injectable } from '@angular/core';
import {
  HttpContextToken,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import { QueryClient } from '@tanstack/query-core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

const READ_METHODS = ['GET', 'HEAD', 'OPTIONS'];

/**
 * Set on a request that uses POST/PUT/PATCH/DELETE without changing data shown in lists (a search sent
 * as POST, OTP or auth calls), so it doesn't clear the cache:
 *
 * ```ts
 * this.http.post(url, body, { context: new HttpContext().set(SKIP_QUERY_INVALIDATION, true) });
 * ```
 */
export const SKIP_QUERY_INVALIDATION = new HttpContextToken<boolean>(() => false);

/**
 * After any successful write (POST, PUT, PATCH, DELETE), assumes cached data may be out of date:
 *
 * - Queries not on screen are dropped, so going back to a list after an edit shows the loading
 *   state and fresh rows, never the pre-edit rows.
 * - Queries on screen refetch, and keep showing their rows until the new ones arrive.
 *
 * Doing this for every write, rather than per service, covers writes that change other data
 * (editing a shift changes its timecards; processing payroll changes timecard statuses).
 * Requests that don't change list data can opt out with `SKIP_QUERY_INVALIDATION`.
 */
@Injectable()
export class InvalidateOnWriteInterceptor implements HttpInterceptor {
  constructor(private readonly queryClient: QueryClient) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (READ_METHODS.includes(req.method) || req.context.get(SKIP_QUERY_INVALIDATION)) return next.handle(req);

    return next.handle(req).pipe(
      tap(event => {
        if (!(event instanceof HttpResponse)) return;
        this.queryClient.removeQueries({ type: 'inactive' });
        this.queryClient.invalidateQueries();
      })
    );
  }
}
